import { useCssVars as _useCssVars, defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, vModelText as _vModelText, withDirectives as _withDirectives, normalizeClass as _normalizeClass, withModifiers as _withModifiers, toDisplayString as _toDisplayString, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "audio-trimmer" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("div", { class: "audio-trimmer__playhead-overlay" }, null, -1)
const _hoisted_3 = /*#__PURE__*/_createElementVNode("div", { class: "audio-trimmer__playhead-hover-overlay" }, null, -1)
const _hoisted_4 = ["min", "max", "step"]
const _hoisted_5 = /*#__PURE__*/_createElementVNode("div", { class: "audio-trimmer__bracket" }, null, -1)
const _hoisted_6 = /*#__PURE__*/_createElementVNode("div", { class: "audio-trimmer__inner-bracket" }, null, -1)
const _hoisted_7 = { class: "audio-trimmer__middle-handle" }
const _hoisted_8 = ["value", "onKeydown"]
const _hoisted_9 = /*#__PURE__*/_createElementVNode("div", { class: "audio-trimmer__outside-loop-overlay audio-trimmer__outside-loop-overlay--left" }, null, -1)
const _hoisted_10 = /*#__PURE__*/_createElementVNode("div", { class: "audio-trimmer__grabber" }, null, -1)
const _hoisted_11 = [
  _hoisted_10
]
const _hoisted_12 = /*#__PURE__*/_createElementVNode("div", { class: "audio-trimmer__outside-loop-overlay audio-trimmer__outside-loop-overlay--right" }, null, -1)
const _hoisted_13 = /*#__PURE__*/_createElementVNode("div", { class: "audio-trimmer__grabber" }, null, -1)
const _hoisted_14 = [
  _hoisted_13
]

import Waveform from '@/components/preview/Waveform.vue'
import useAudioEngine from '@/composables/UseAudioEngine'
import timeFormat from '@/utils/time-format'
import CardCorner from '@/assets/CardCorner.svg'
import Gripper from '@/components/common/Gripper.vue'
import { ComponentPublicInstance, computed, onMounted, Ref, ref, watch } from 'vue'

type Bracket = 'left' | 'right'


export default /*#__PURE__*/_defineComponent({
  __name: 'AudioTrimmer',
  props: {
    currentTime: null,
    totalTime: null,
    audioBufferWaveformData: null,
    resetTrimFlag: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as {
  currentTime: number
  totalTime: number
  audioBufferWaveformData: Float32Array
  resetTrimFlag: boolean
};

_useCssVars(_ctx => ({
  "d2f1a50c": (_unref(defaultCursor)),
  "83d78d32": (_unref(loopStartPositionStyle)),
  "f7501464": (_unref(loopEndPositionStyle)),
  "62cba876": (_unref(pointerCursor)),
  "4a23813c": (_unref(leftMiddleBracketStyle)),
  "60fe941d": (_unref(rightMiddleBracketStyle)),
  "19774059": (_unref(middleHandleBorderRadiusStyle)),
  "75d9728a": (_unref(handleCursor)),
  "226e2940": (_unref(textCursor)),
  "672338ca": (_unref(leftInnerBracketStyle)),
  "d219e900": (_unref(rightInnerBracketStyle)),
  "7ad08c2d": (_unref(leftBracketStyle)),
  "1601c928": (_unref(rightBracketStyle)),
  "f80eb720": (_unref(handleWidthStyle)),
  "6a9f8965": (_unref(playHeadOverlayStartPositionStyle)),
  "80e91202": (_unref(playHeadHoverOverlayEndPosition))
}))



const { seekAudio, trimAudio, untrimmedAudioDurationSeconds, loopStartTime, loopEndTime, loadState } = useAudioEngine()

const handleWidth = 10
const middleHandleWidth = 62
const minLoopLength = computed(() => Math.ceil(scrubberWidth.value / untrimmedAudioDurationSeconds.value)) // calculates how wide in pixels 1 second is based on scrubber width

const audioSeekSliderModel = ref(0)
const sliderDragging = ref(false)
const scrubber = ref<HTMLDivElement | null>()
const leftBracketElement = ref<HTMLDivElement | null>()
const rightBracketElement = ref<HTMLDivElement | null>()
const leftGripper = ref<ComponentPublicInstance | null>()
const rightGripper = ref<ComponentPublicInstance | null>()

const isDraggingHandle = ref(false)
const inputHover = ref(false)
const durationInputUpdated = ref(false)

const handleCursor = computed(() => isDraggingHandle.value ? 'grabbing' : 'grab')
const pointerCursor = computed(() => isDraggingHandle.value ? 'grabbing' : 'pointer')
const defaultCursor = computed(() => isDraggingHandle.value ? 'grabbing' : 'default')
const textCursor = computed(() => isDraggingHandle.value ? 'grabbing' : 'text')
const untrimmedAudioDurationFormatted = computed(() => timeFormat(untrimmedAudioDurationSeconds.value))
const loopDurationInputModel = ref(untrimmedAudioDurationFormatted.value)
const loopStartPercentage = ref(0) // The percentage through the track that the loop starts
const loopEndPercentage = ref(100) // The percentage through the track that the loop ends
const scrubberWidth = ref(1000)

const loopStartPositionStyle = computed(() => `${loopStartPercentage.value}%`)
const loopEndPositionStyle = computed(() => `${100 - loopEndPercentage.value}%`)
const leftBracketStyle = computed(() => `calc(${loopStartPercentage.value}% - ${handleWidth}px)`)
const rightBracketStyle = computed(() => `calc(${100 - loopEndPercentage.value}% - ${handleWidth}px)`)
const leftInnerBracketStyle = computed(() => `calc(${loopStartPercentage.value}% - ${handleWidth - 8}px)`)
const rightInnerBracketStyle = computed(() => `calc(${100 - loopEndPercentage.value}% - ${handleWidth - 8}px)`)
const centerPosition = computed(() => (loopEndPercentage.value + loopStartPercentage.value) / 2)
const leftMiddleBracketStyle = computed(() => `calc(${centerPosition.value}% - ${middleHandleWidth / 2}px)`)
const rightMiddleBracketStyle = computed(() => `calc((${100 - centerPosition.value}% - ${middleHandleWidth / 2}px)`)
const loopStartLabel = computed(() => timeFormat(loopStartPercentage.value / 100 * untrimmedAudioDurationSeconds.value))
const loopEndLabel = computed(() => timeFormat(loopEndPercentage.value / 100 * untrimmedAudioDurationSeconds.value))
const loopStartLabelPositionStyle = computed(() => {
  if ((loopEndPercentage.value - loopStartPercentage.value) / 100 * scrubberWidth.value > middleHandleWidth + 32) {
    return {
      left: leftBracketStyle.value,
      top: '-19px'
    }
  } else {
    return {
      left: `calc(${centerPosition.value}% - ${middleHandleWidth / 2}px)`,
      top: '-36px'
    }
  }
})
const loopEndLabelPositionStyle = computed(() => {
  if ((loopEndPercentage.value - loopStartPercentage.value) / 100 * scrubberWidth.value > middleHandleWidth + 32) {
    return {
      right: rightBracketStyle.value,
      top: '-19px'
    }
  } else {
    return {
      right: `calc(${100 - centerPosition.value}% - ${middleHandleWidth / 2}px)`,
      top: '-36px'
    }
  }
})
const showMiddleBracketCurve = computed(() => (loopEndPercentage.value - loopStartPercentage.value) / 100 * scrubberWidth.value > middleHandleWidth)
const middleHandleBorderRadiusStyle = computed(() => showMiddleBracketCurve.value ? '4px 4px 0px 0px': '4px')
const playHeadPosition = computed(() => audioSeekSliderModel.value / untrimmedAudioDurationSeconds.value * 100)
const scrubberMousePosition = computed(() => mouseXPosition.value / scrubberWidth.value * 100 + loopStartPercentage.value)
const playHeadHoverOverlayEndPosition = computed(() => { 
  if (inputHover.value && !isDraggingHandle.value && scrubberMousePosition.value < playHeadPosition.value) {
    return `${100 - scrubberMousePosition.value}%`
  }

  return 100 - playHeadPosition.value + '%'
})

const playHeadOverlayStartPositionStyle = computed(() => {
  if (inputHover.value && !isDraggingHandle.value && scrubberMousePosition.value > playHeadPosition.value) {
    return `${scrubberMousePosition.value}%`
  }

  return Math.max(playHeadPosition.value, loopStartPercentage.value) + '%'
})

const onChangeLoopDuration = () => {
  if (durationInputUpdated.value) {
    const loopDuration = getTimeInputSeconds()
    // 0 loop duration indicates an invalid input
    if (loopDuration > 0) {
      // loopEnd must not go beyond the end of the track
      // Add 0.000001 just to account for some rounding errors that could result in the duration being a second off because we are rounding down
      loopEndPercentage.value = Math.min(loopStartPercentage.value + loopDuration / untrimmedAudioDurationSeconds.value * 100 + 0.0000001, 100)
    }
    durationInputUpdated.value = false
  }

  onTrimAudio()
}

const onUpdateLoopDuration = (e: KeyboardEvent) => {
  if (loopDurationInputModel.value.length < 4 && (e.key === '0' || parseInt(e.key))) {
    loopDurationInputModel.value += e.key
    if (loopDurationInputModel.value.length === 1) {
      loopDurationInputModel.value += ':'
    }
    durationInputUpdated.value = true
  } else if (e.key === 'Backspace') {
    loopDurationInputModel.value = loopDurationInputModel.value.length === 2 ? '' : loopDurationInputModel.value.substring(0, loopDurationInputModel.value.length - 1)
  } else if (e.key ==='Enter') {
    onChangeLoopDuration()
  }
}

const getTimeInputSeconds = () => {
  const times = loopDurationInputModel.value.split(':')
  return parseInt(times[0]) * 60 + parseInt(times[1])
}

const onTrimAudio = () => {
  trimAudio(loopStartPercentage.value / 100 * untrimmedAudioDurationSeconds.value, loopEndPercentage.value / 100 * untrimmedAudioDurationSeconds.value)
  loopDurationInputModel.value = timeFormat(getLoopDuration())
}

const resetTrim = () => {
  loopStartPercentage.value = 0
  loopEndPercentage.value = 100
  onTrimAudio()
}

watch(() => props.resetTrimFlag, () => {
  resetTrim()
})

watch(() => loadState.value, () => {
  if (loadState.value === 'loaded') {
    resetTrim()
  }
})
  
watch(() => props.currentTime, () => {
  if (!sliderDragging.value) {
    audioSeekSliderModel.value = props.currentTime + loopStartTime.value
  }
})

const getLoopDuration = () => {
  return (loopEndPercentage.value - loopStartPercentage.value) / 100 * untrimmedAudioDurationSeconds.value
}

const onUpdateAudioSlider = () => {
  sliderDragging.value = true
}

const mouseXPosition = ref(0)
const trackMousePosition = (e: MouseEvent) => {
  mouseXPosition.value = e.offsetX
}

onMounted(() => {
  if (leftBracketElement.value && rightBracketElement.value && scrubber.value && leftGripper.value && rightGripper.value) {
    initBracketDrag(leftBracketElement.value, loopStartPercentage, 'left')
    initBracketDrag(rightBracketElement.value, loopEndPercentage, 'right')
    scrubber.value.onmousemove = trackMousePosition
    leftGripper.value.$el.onmousedown = rightGripper.value.$el.onmousedown = (e: MouseEvent) => {
      if (e.button === 0) {
        isDraggingHandle.value = true
        const leftInitialPosition = loopStartPercentage.value / 100 * scrubberWidth.value
        const start = e.pageX
  
        document.body.onmousemove = (e) => {
          const end = e.pageX
          const diff = end-start
          let newPosition = Math.max(diff + leftInitialPosition, 0)
          // Ensure it's less than the end of the scrubber minus the duration
          const loopDurationPercentage = props.totalTime / untrimmedAudioDurationSeconds.value * 100
          const loopDurationLength = loopDurationPercentage / 100 * scrubberWidth.value
          newPosition = Math.min(newPosition, scrubberWidth.value - loopDurationLength)

          // Convert to percentage
          loopStartPercentage.value = (newPosition / scrubberWidth.value) * 100
          loopEndPercentage.value = loopStartPercentage.value + loopDurationPercentage
        }

        window.onmouseup = () => {
          isDraggingHandle.value = false
          onTrimAudio()
          document.body.onmousemove = null
          window.onmouseup = null
        }
      }
    }

    scrubberWidth.value = scrubber.value.clientWidth
    new ResizeObserver(() => {
      if (scrubber.value) {
        scrubberWidth.value = scrubber.value.clientWidth
      }
    }).observe(scrubber.value)
  }
})

const initBracketDrag = (trimBracket: HTMLDivElement, loopModel: Ref<number>, type: Bracket) => {
  trimBracket.onmousedown = (e) => {
    if (e.button === 0) {
      isDraggingHandle.value = true
      const initialPosition = loopModel.value * scrubberWidth.value / 100
      const start = e.pageX
  
      document.body.onmousemove = (e) => {
        const end = e.pageX
        // Calculate the number of pixels dragged
        const diff = end-start
        // Calculate new position as pixels
        let newPosition = diff + initialPosition
        // Ensure it's less than the end of the scrubber
        if (type === 'left') {
          newPosition = Math.max(newPosition, 0)
          newPosition = Math.min(newPosition, loopEndPercentage.value / 100 * scrubberWidth.value - minLoopLength.value)
        } else if (type === 'right') {
          newPosition = Math.min(newPosition, scrubberWidth.value)
          newPosition = Math.max(newPosition, loopStartPercentage.value / 100 * scrubberWidth.value + minLoopLength.value)
        }

        // Convert to percentage
        loopModel.value = (newPosition / scrubberWidth.value) * 100
        loopDurationInputModel.value = timeFormat(getLoopDuration())
      }

      window.onmouseup = () => {
        isDraggingHandle.value = false
        onTrimAudio()
        document.body.onmousemove = null
        window.onmouseup = null
      }
    }
  }
}

const onChangeAudioSlider = () => {
  sliderDragging.value = false
  seekAudio(audioSeekSliderModel.value - loopStartTime.value)
}

const handleWidthStyle = computed(() => handleWidth + 'px')


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(Waveform, {
      class: "audio-trimmer__waveform",
      "audio-buffer-waveform-data": __props.audioBufferWaveformData
    }, null, 8, ["audio-buffer-waveform-data"]),
    _hoisted_2,
    _hoisted_3,
    _createElementVNode("div", {
      class: "audio-trimmer__scrubber",
      ref_key: "scrubber",
      ref: scrubber
    }, [
      _withDirectives(_createElementVNode("input", {
        class: "audio-trimmer__slider",
        type: "range",
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((audioSeekSliderModel).value = $event)),
        min: _unref(loopStartTime),
        max: _unref(loopEndTime),
        step: 0.01,
        onInput: _cache[1] || (_cache[1] = ($event: any) => (onUpdateAudioSlider())),
        onChange: _cache[2] || (_cache[2] = ($event: any) => (onChangeAudioSlider())),
        onMouseover: _cache[3] || (_cache[3] = ($event: any) => (inputHover.value = true)),
        onMouseleave: _cache[4] || (_cache[4] = ($event: any) => (inputHover.value = false))
      }, null, 40, _hoisted_4), [
        [
          _vModelText,
          audioSeekSliderModel.value,
          void 0,
          { number: true }
        ]
      ])
    ], 512),
    _hoisted_5,
    _hoisted_6,
    _createElementVNode("div", _hoisted_7, [
      _createVNode(_unref(CardCorner), {
        class: _normalizeClass(['middle-handle__curve', 'middle-handle__left-curve', {'middle-handle__curve--hidden': !_unref(showMiddleBracketCurve)}])
      }, null, 8, ["class"]),
      _createVNode(Gripper, {
        class: "audio-trimmer__gripper",
        ref_key: "leftGripper",
        ref: leftGripper
      }, null, 512),
      _createElementVNode("input", {
        class: "audio-trimmer__duration-input",
        type: "text",
        required: "",
        value: loopDurationInputModel.value,
        onBlur: onChangeLoopDuration,
        onKeydown: _withModifiers(onUpdateLoopDuration, ["prevent"]),
        onFocus: _cache[5] || (_cache[5] = ($event: any) => (loopDurationInputModel.value = ''))
      }, null, 40, _hoisted_8),
      _createVNode(Gripper, {
        class: "audio-trimmer__gripper",
        ref_key: "rightGripper",
        ref: rightGripper
      }, null, 512),
      _createVNode(_unref(CardCorner), {
        class: _normalizeClass(['middle-handle__curve', 'middle-handle__right-curve', {'middle-handle__curve--hidden': !_unref(showMiddleBracketCurve)}])
      }, null, 8, ["class"])
    ]),
    _hoisted_9,
    _createElementVNode("label", {
      class: "audio-trimmer__time-label",
      style: _normalizeStyle(_unref(loopStartLabelPositionStyle))
    }, _toDisplayString(_unref(loopStartLabel)), 5),
    _createElementVNode("div", {
      class: "audio-trimmer__handle audio-trimmer__handle--left",
      ref_key: "leftBracketElement",
      ref: leftBracketElement
    }, _hoisted_11, 512),
    _hoisted_12,
    _createElementVNode("label", {
      class: "audio-trimmer__time-label",
      style: _normalizeStyle(_unref(loopEndLabelPositionStyle))
    }, _toDisplayString(_unref(loopEndLabel)), 5),
    _createElementVNode("div", {
      class: "audio-trimmer__handle audio-trimmer__handle--right",
      ref_key: "rightBracketElement",
      ref: rightBracketElement
    }, _hoisted_14, 512)
  ]))
}
}

})